import { RecipientType, NotificationEntityType, ReminderRecipientGroupCodes } from "@/models/enum"
import { Reminder } from "@/models/interface"
import { TranslationKey } from "@/types/translations"

/**
 * Get the appropriate label for a reminder based on recipient type and entity types
 * For workers: Uses RecipientGroupCodes to determine worker type
 * For others: Uses EntityTypes to determine timesheet/expense type
 */
export const getReminderDisplayLabel = (reminder: Reminder, t: (key: TranslationKey, variables?: Record<string, any>) => string): string => {
    // For workers, use RecipientGroupCodes
    if (reminder.RecipientType === RecipientType.WORKER) {
        const recipientGroupCodes = reminder.RegularReminder.RecipientGroupCodes
        
        // Check if all three worker types are present
        const hasOffline = recipientGroupCodes.includes(ReminderRecipientGroupCodes.OfflineTimesheetWorks)
        const hasOnline = recipientGroupCodes.includes(ReminderRecipientGroupCodes.OnlineTimesheetWorkers)
        const hasVms = recipientGroupCodes.includes(ReminderRecipientGroupCodes.VmsTimesheetWorkers)
        
        if (hasOffline && hasOnline && hasVms) {
            return t("dashboard.labels.allWorkers")
        }
        
        // Handle specific worker types
        if (hasOnline && !hasOffline && !hasVms) {
            return t("dashboard.labels.onlineWorkers")
        }
        if (hasOffline && !hasOnline && !hasVms) {
            return t("dashboard.labels.offlineWorkers")
        }
        if (hasVms && !hasOnline && !hasOffline) {
            return t("dashboard.labels.vmsWorkers")
        }
        
        // For multiple but not all three, fallback to allWorkers
        return t("dashboard.labels.allWorkers")
    }
    
    // For clients and internal, use EntityTypes
    if (reminder.RecipientType === RecipientType.CLIENT || reminder.RecipientType === RecipientType.INTERNAL) {
        const entityTypes = reminder.EntityTypes || []
        
        const hasTimesheet = entityTypes.includes(NotificationEntityType.TIMESHEET)
        const hasExpense = entityTypes.includes(NotificationEntityType.EXPENSE_CLAIM)
        const hasTimesheetExpense = entityTypes.includes(NotificationEntityType.TIMESHEET_EXPENSE)
        
        if (hasTimesheetExpense || (hasTimesheet && hasExpense)) {
            return t("dashboard.labels.timesheetsAndExpenses")
        } else if (hasTimesheet) {
            return t("dashboard.labels.timesheets")
        } else if (hasExpense) {
            return t("dashboard.labels.expenses")
        }
    }
    
    return t("dashboard.labels.timesheets")
}